import {useEffect, useState} from "react";

import {clearCache, withCache} from "../cache";
import {useApi} from "../PlayMakersProvider";
import {
  CategoryParentType,
  CategoryPostResponseType,
  CategoryType
} from "../types/categories.types";
import {usePagination} from "./usePagination";

type CategoryFetchMethod = (quiet?: boolean) => Promise<void>;
type CategoryRenameMethod = (name: string) => Promise<CategoryPostResponseType>;
type CategoryCreateMethod = (name: string, parentId?: string, projectId?: string) => Promise<CategoryPostResponseType>;
type CategoryDeleteMethod = () => Promise<void>;

interface UseCategoryReturn extends Partial<CategoryType> {
  category: CategoryType | null;
  parents: CategoryParentType[];
  fetched: boolean;
  rename: CategoryRenameMethod;
  create: CategoryCreateMethod;
  delete: CategoryDeleteMethod;
}

// TODO: There's no endpoint to fetch a single category by ID yet.
// We are only using the useCategories hook for now
export const useCategory = (categoryId?: string): UseCategoryReturn => {
  const {getParentCategories, createCategory, deleteCategory, renameCategory, getCategories} = useApi();

  const [fetched, setFetched] = useState<boolean>(false);
  const [parents, setParents] = useState<CategoryParentType[]>([]);
  const [category, setCategory] = useState<CategoryType | null>(null);

  const fetchCategory:CategoryFetchMethod = async(quiet: boolean = false) => {
    if(!categoryId) return;
    setFetched(quiet);
    try {
      setParents(
        (await withCache(getParentCategories, [categoryId], 300_000)).slice(0, -1)
      );
      // @ts-ignore
      setCategory(await withCache(getCategories, [categoryId, undefined, {}], 300_000));
      setFetched(true);
    } catch (error) {
      console.error("Error fetching category", categoryId, error);
      throw new Error(`Error fetching category '${categoryId}'`, {
        cause: error
      });
    }
  };

  const create:CategoryCreateMethod = async(name, parentId, projectId) => {
    try {
      const cat = await createCategory(name, parentId, projectId);
      clearCache("getCategories");
      clearCache("getParentCategories");
      // @ts-ignore
      setCategory(await withCache(getCategories, [cat.id], 300_000));
      setParents(
        (await withCache(getParentCategories, [cat.id], 300_000)).slice(0, -1)
      );
      return cat;
    } catch (error) {
      console.error(`Error attempting to create category '${name}'`, error);
      throw new Error(`Error attempting to create category '${name}'`, {
        cause: error
      });
    } finally {
      setFetched(true);
    }
  };

  const rename: CategoryRenameMethod = async name => {
    try {
      if(!category?.id) throw new Error("No category to rename");

      const newCategory = await renameCategory(category?.id, name);

      // @ts-ignore
      setCategory(await withCache(getCategories, [categoryId], 300_000));

      return newCategory;
    } catch (error) {
      console.error(`Error attempting to rename category '${name}'`, error);
      throw new Error(`Error attempting to rename category '${name}'`, {
        cause: error
      });
    }
  };

  const deleteCat :CategoryDeleteMethod = async() => {
    try {
      if(!category?.id) throw new Error("No category to delete");

      await deleteCategory(category?.id);
    } catch (error) {
      console.error(
        `Error attempting to delete category '${category?.name}'`,
        error
      );
      throw new Error(
        `Error attempting to delete category '${category?.name}'`,
        {
          cause: error
        }
      );
    }
  };

  useEffect(() => {
    fetchCategory();
  }, [categoryId]);

  return {
    ...category,
    category,
    create,
    delete: deleteCat,
    fetched,
    parents,
    rename
  };
};

type CategoriesFetchMoreMethod = (refresh?: boolean) => Promise<void>;
type CategoriesCreateMethod = (name: string, parentId?: string) => Promise<CategoryPostResponseType>;
type CategoriesFetchMethod = (quiet?: boolean) => Promise<CategoryType[]>;

interface UseCategoriesReturn {
  categories: CategoryType[];
  create: CategoriesCreateMethod
  fetched: boolean;
  hasMore: boolean;
  currentPage: number;
  fetchMore: CategoriesFetchMoreMethod;
  refetch: CategoriesFetchMethod;
}

// TODO The hook below needs to be greatly enhanced
// However the API side might also move a bit, therefore keeping this pending for now
export const useCategories = (projectId: string | undefined, params: {[key:string]: any} = {}): UseCategoriesReturn => {
  const {getCategories, createCategory} = useApi();
  const [fetched, setFetched] = useState<boolean>(false);
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const {appendNextPage, refreshLastPage, checkHasMore, currentPage} =
    usePagination<any>(setCategories, setHasMore, hasMore,  params?.page, params?.limit);


  const fetchCategories: CategoriesFetchMethod = async(quiet = false) => {
    setFetched(quiet);
    try {
      const newCategories = checkHasMore(await getCategories(undefined, projectId));
      setCategories(newCategories);
      return newCategories;
    } catch (error) {
      console.error("Error fetching categories", error);
      throw new Error("Error fetching categories", {cause: error});
    } finally {
      setFetched(true);
    }
  };

  const fetchMore: CategoriesFetchMoreMethod = async refresh => {
    try {
      setFetched(false);
      if(refresh) {
        await refreshLastPage(async appendParams =>
          await getCategories(undefined, projectId, appendParams)
        );
      } else {
        await appendNextPage(async appendParams =>
          await getCategories(undefined, projectId, appendParams)
        );
      }
    } catch (error) {
      console.error("Error fetching more schemas", error);
      throw new Error("Error fetching more schemas", {cause: error});
    } finally {
      setFetched(true);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [projectId, params?.page]);

  return {
    categories,
    create: createCategory,
    currentPage,
    fetchMore,
    fetched,
    hasMore,
    refetch: fetchCategories
  };
};
